import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Skill, getSkillColor } from '../../types/Skill';
import { Goal } from '../../types/Goal';
import './SkillRoadmap.css';

interface SkillRoadmapProps {
  skills: Skill[];
  nonMappingGoals?: Goal[];
  viewMode?: 'years' | 'months';
}

interface YearData {
  year: number;
  level: number | null;
  isCurrent: boolean;
  isPast: boolean;
  isFuture: boolean;
  goals?: Goal[];
}

interface MonthData {
  month: number;
  monthName: string;
  isCurrent: boolean;
  isPast: boolean;
  isFuture: boolean;
  goals?: Goal[];
}

const SkillRoadmap: React.FC<SkillRoadmapProps> = ({ skills, nonMappingGoals = [], viewMode = 'years' }) => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  // Convert hex color to HSL for sorting
  const hexToHsl = (hex: string): { h: number; s: number; l: number } => {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Parse RGB
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      
      switch (max) {
        case r:
          h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
          break;
        case g:
          h = ((b - r) / d + 2) / 6;
          break;
        case b:
          h = ((r - g) / d + 4) / 6;
          break;
      }
    }

    return { h: h * 360, s, l };
  };

  // Sort skills by color (by hue, then saturation, then lightness)
  // Skills without colors go to the end
  const sortedSkills = [...skills].sort((a, b) => {
    const colorA = getSkillColor(a);
    const colorB = getSkillColor(b);

    // Skills without colors go to the end
    if (!colorA && !colorB) return 0;
    if (!colorA) return 1;
    if (!colorB) return -1;

    // Convert to HSL for better color sorting
    const hslA = hexToHsl(colorA);
    const hslB = hexToHsl(colorB);

    // Sort by hue first (0-360)
    if (Math.abs(hslA.h - hslB.h) > 0.01) {
      return hslA.h - hslB.h;
    }

    // Then by saturation
    if (Math.abs(hslA.s - hslB.s) > 0.01) {
      return hslA.s - hslB.s;
    }

    // Finally by lightness
    return hslA.l - hslB.l;
  });

  // Get goals mapped by their date range, and all-year goals (without start/end dates)
  const getGoalsByYear = (skill: Skill): { goalsByYear: Map<number, Goal[]>; allYearGoals: Goal[] } => {
    const goalsByYear = new Map<number, Goal[]>();
    const allYearGoals: Goal[] = [];
    
    if (!skill.linkedGoals || skill.linkedGoals.length === 0) {
      return { goalsByYear, allYearGoals };
    }

    skill.linkedGoals.forEach((goal) => {
      // Check if goal is missing startedDate and/or targetDate
      const hasStartedDate = goal.startedDate && goal.startedDate.trim() !== '';
      const hasTargetDate = goal.targetDate && goal.targetDate.trim() !== '';
      
      // If missing either date, show in all years
      if (!hasStartedDate || !hasTargetDate) {
        allYearGoals.push(goal);
        console.debug(`Goal "${goal.title}" (${goal.id}) missing dates - will show in all years`);
        return;
      }

      // Goal has both dates, map to all years between start and end date (inclusive)
      try {
        // Parse both dates (assuming they're in ISO format or similar)
        // Handle timezone conversion if needed (dates stored in UTC)
        const startedDate = new Date(goal.startedDate!);
        const targetDate = new Date(goal.targetDate!);
        
        if (isNaN(startedDate.getTime())) {
          console.warn(`Invalid startedDate for goal ${goal.id}: ${goal.startedDate}`);
          allYearGoals.push(goal);
          return;
        }
        
        if (isNaN(targetDate.getTime())) {
          console.warn(`Invalid targetDate for goal ${goal.id}: ${goal.targetDate}`);
          allYearGoals.push(goal);
          return;
        }
        
        const startYear = startedDate.getFullYear();
        const endYear = targetDate.getFullYear();
        
        // Map goal to all years between start and end (inclusive)
        for (let year = startYear; year <= endYear; year++) {
          if (!goalsByYear.has(year)) {
            goalsByYear.set(year, []);
          }
          goalsByYear.get(year)!.push(goal);
        }
        
        console.debug(`Mapped goal "${goal.title}" to years ${startYear}-${endYear} (${endYear - startYear + 1} years)`);
      } catch (err) {
        // Invalid date, skip this goal or treat as all-year
        console.warn(`Invalid dates for goal ${goal.id}: startedDate=${goal.startedDate}, targetDate=${goal.targetDate}`, err);
        allYearGoals.push(goal);
      }
    });

    return { goalsByYear, allYearGoals };
  };

  // Get non-mapping goals mapped by their date range, and all-year goals (without start/end dates)
  const getNonMappingGoalsByYear = (): { goalsByYear: Map<number, Goal[]>; allYearGoals: Goal[] } => {
    const goalsByYear = new Map<number, Goal[]>();
    const allYearGoals: Goal[] = [];
    
    if (!nonMappingGoals || nonMappingGoals.length === 0) {
      return { goalsByYear, allYearGoals };
    }

    nonMappingGoals.forEach((goal) => {
      // Check if goal is missing startedDate and/or targetDate
      const hasStartedDate = goal.startedDate && goal.startedDate.trim() !== '';
      const hasTargetDate = goal.targetDate && goal.targetDate.trim() !== '';
      
      // If missing either date, show in all years
      if (!hasStartedDate || !hasTargetDate) {
        allYearGoals.push(goal);
        console.debug(`Non-mapping goal "${goal.title}" (${goal.id}) missing dates - will show in all years`);
        return;
      }

      // Goal has both dates, map to all years between start and end date (inclusive)
      try {
        // Parse both dates (assuming they're in ISO format or similar)
        // Handle timezone conversion if needed (dates stored in UTC)
        const startedDate = new Date(goal.startedDate!);
        const targetDate = new Date(goal.targetDate!);
        
        if (isNaN(startedDate.getTime())) {
          console.warn(`Invalid startedDate for non-mapping goal ${goal.id}: ${goal.startedDate}`);
          allYearGoals.push(goal);
          return;
        }
        
        if (isNaN(targetDate.getTime())) {
          console.warn(`Invalid targetDate for non-mapping goal ${goal.id}: ${goal.targetDate}`);
          allYearGoals.push(goal);
          return;
        }
        
        const startYear = startedDate.getFullYear();
        const endYear = targetDate.getFullYear();
        
        // Map goal to all years between start and end (inclusive)
        for (let year = startYear; year <= endYear; year++) {
          if (!goalsByYear.has(year)) {
            goalsByYear.set(year, []);
          }
          goalsByYear.get(year)!.push(goal);
        }
        
        console.debug(`Mapped non-mapping goal "${goal.title}" to years ${startYear}-${endYear} (${endYear - startYear + 1} years)`);
      } catch (err) {
        // Invalid date, skip this goal or treat as all-year
        console.warn(`Invalid dates for non-mapping goal ${goal.id}: startedDate=${goal.startedDate}, targetDate=${goal.targetDate}`, err);
        allYearGoals.push(goal);
      }
    });

    return { goalsByYear, allYearGoals };
  };

  // Calculate year range from all skills' goals and non-mapping goals
  const getYearRange = (): { startYear: number; endYear: number; years: number[] } => {
    const currentYear = new Date().getFullYear();
    let startYear = currentYear;
    let endYear = currentYear + 2; // Default to show current year + 2 years ahead

    // Find the earliest and latest years from goals (considering both start and end dates)
    sortedSkills.forEach((skill) => {
      if (skill.linkedGoals && skill.linkedGoals.length > 0) {
        skill.linkedGoals.forEach((goal) => {
          // Check both startedDate and targetDate to find the full range
          if (goal.startedDate) {
            try {
              const date = new Date(goal.startedDate);
              if (!isNaN(date.getTime())) {
                const year = date.getFullYear();
                startYear = Math.min(startYear, year);
                endYear = Math.max(endYear, year);
              }
            } catch (err) {
              // Invalid date, skip
            }
          }
          if (goal.targetDate) {
            try {
              const date = new Date(goal.targetDate);
              if (!isNaN(date.getTime())) {
                const year = date.getFullYear();
                startYear = Math.min(startYear, year);
                endYear = Math.max(endYear, year);
              }
            } catch (err) {
              // Invalid date, skip
            }
          }
        });
      }
    });

    // Also consider non-mapping goals for year range
    if (nonMappingGoals && nonMappingGoals.length > 0) {
      nonMappingGoals.forEach((goal) => {
        if (goal.startedDate) {
          try {
            const date = new Date(goal.startedDate);
            if (!isNaN(date.getTime())) {
              const year = date.getFullYear();
              startYear = Math.min(startYear, year);
              endYear = Math.max(endYear, year);
            }
          } catch (err) {
            // Invalid date, skip
          }
        }
        if (goal.targetDate) {
          try {
            const date = new Date(goal.targetDate);
            if (!isNaN(date.getTime())) {
              const year = date.getFullYear();
              startYear = Math.min(startYear, year);
              endYear = Math.max(endYear, year);
            }
          } catch (err) {
            // Invalid date, skip
          }
        }
      });
    }

    // Ensure we show at least current year and 2 years ahead
    startYear = Math.min(startYear, currentYear);
    endYear = Math.max(endYear, currentYear + 2);

    const years: number[] = [];
    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }

    return { startYear, endYear, years };
  };

  // Get year data for a skill
  const getYearDataForSkill = (skill: Skill, year: number): YearData => {
    const currentYear = new Date().getFullYear();
    const isCurrent = year === currentYear;
    const isPast = year < currentYear;
    const isFuture = year > currentYear;

    // Helper to safely convert to number
    const toNumber = (value: any): number | null => {
      if (value === null || value === undefined) return null;
      const num = typeof value === 'string' ? parseFloat(value) : Number(value);
      return isNaN(num) ? null : num;
    };

    // Get goals for this year (both year-specific and all-year goals)
    const { goalsByYear, allYearGoals } = getGoalsByYear(skill);
    const yearSpecificGoals = goalsByYear.get(year) || [];
    
    // Combine year-specific goals with all-year goals
    const goalsForYear = [...yearSpecificGoals, ...allYearGoals];

    // Show current level for current year, null for others
    return {
      year,
      level: isCurrent ? toNumber(skill.current_level) : null,
      isCurrent,
      isPast,
      isFuture,
      goals: goalsForYear.length > 0 ? goalsForYear : undefined
    };
  };

  // Get year data for non-mapping goals
  const getYearDataForNonMappingGoals = (year: number): { goals?: Goal[] } => {
    const { goalsByYear, allYearGoals } = getNonMappingGoalsByYear();
    const yearSpecificGoals = goalsByYear.get(year) || [];
    
    // Combine year-specific goals with all-year goals
    const goalsForYear = [...yearSpecificGoals, ...allYearGoals];

    return {
      goals: goalsForYear.length > 0 ? goalsForYear : undefined
    };
  };

  // Get goals mapped by month for a skill within the current year
  const getGoalsByMonth = (skill: Skill): { goalsByMonth: Map<number, Goal[]>; allMonthGoals: Goal[] } => {
    const goalsByMonth = new Map<number, Goal[]>();
    const allMonthGoals: Goal[] = [];
    
    if (!skill.linkedGoals || skill.linkedGoals.length === 0) {
      return { goalsByMonth, allMonthGoals };
    }

    skill.linkedGoals.forEach((goal) => {
      const hasStartedDate = goal.startedDate && goal.startedDate.trim() !== '';
      const hasTargetDate = goal.targetDate && goal.targetDate.trim() !== '';
      
      if (!hasStartedDate || !hasTargetDate) {
        allMonthGoals.push(goal);
        return;
      }

      try {
        const startedDate = new Date(goal.startedDate!);
        const targetDate = new Date(goal.targetDate!);
        
        if (isNaN(startedDate.getTime()) || isNaN(targetDate.getTime())) {
          allMonthGoals.push(goal);
          return;
        }

        const startYear = startedDate.getFullYear();
        const endYear = targetDate.getFullYear();
        const startMonth = startedDate.getMonth();
        const endMonth = targetDate.getMonth();

        // Only process if goal overlaps with current year
        if (currentYear >= startYear && currentYear <= endYear) {
          // Determine the actual start and end months within the current year
          let actualStartMonth = 0;
          let actualEndMonth = 11;

          if (startYear === currentYear) {
            actualStartMonth = startMonth;
          }
          if (endYear === currentYear) {
            actualEndMonth = endMonth;
          }

          // Map goal to all months between start and end (inclusive) within the current year
          for (let month = actualStartMonth; month <= actualEndMonth; month++) {
            if (!goalsByMonth.has(month)) {
              goalsByMonth.set(month, []);
            }
            goalsByMonth.get(month)!.push(goal);
          }
        }
      } catch (err) {
        console.warn(`Invalid dates for goal ${goal.id}: startedDate=${goal.startedDate}, targetDate=${goal.targetDate}`, err);
        allMonthGoals.push(goal);
      }
    });

    return { goalsByMonth, allMonthGoals };
  };

  // Get non-mapping goals mapped by month for the current year
  const getNonMappingGoalsByMonth = (): { goalsByMonth: Map<number, Goal[]>; allMonthGoals: Goal[] } => {
    const goalsByMonth = new Map<number, Goal[]>();
    const allMonthGoals: Goal[] = [];
    
    if (!nonMappingGoals || nonMappingGoals.length === 0) {
      return { goalsByMonth, allMonthGoals };
    }

    nonMappingGoals.forEach((goal) => {
      const hasStartedDate = goal.startedDate && goal.startedDate.trim() !== '';
      const hasTargetDate = goal.targetDate && goal.targetDate.trim() !== '';
      
      if (!hasStartedDate || !hasTargetDate) {
        allMonthGoals.push(goal);
        return;
      }

      try {
        const startedDate = new Date(goal.startedDate!);
        const targetDate = new Date(goal.targetDate!);
        
        if (isNaN(startedDate.getTime()) || isNaN(targetDate.getTime())) {
          allMonthGoals.push(goal);
          return;
        }

        const startYear = startedDate.getFullYear();
        const endYear = targetDate.getFullYear();
        const startMonth = startedDate.getMonth();
        const endMonth = targetDate.getMonth();

        // Only process if goal overlaps with current year
        if (currentYear >= startYear && currentYear <= endYear) {
          // Determine the actual start and end months within the current year
          let actualStartMonth = 0;
          let actualEndMonth = 11;

          if (startYear === currentYear) {
            actualStartMonth = startMonth;
          }
          if (endYear === currentYear) {
            actualEndMonth = endMonth;
          }

          // Map goal to all months between start and end (inclusive) within the current year
          for (let month = actualStartMonth; month <= actualEndMonth; month++) {
            if (!goalsByMonth.has(month)) {
              goalsByMonth.set(month, []);
            }
            goalsByMonth.get(month)!.push(goal);
          }
        }
      } catch (err) {
        console.warn(`Invalid dates for non-mapping goal ${goal.id}: startedDate=${goal.startedDate}, targetDate=${goal.targetDate}`, err);
        allMonthGoals.push(goal);
      }
    });

    return { goalsByMonth, allMonthGoals };
  };

  // Get month data for a skill
  const getMonthDataForSkill = (skill: Skill, month: number): MonthData => {
    const isCurrent = month === currentMonth;
    const isPast = month < currentMonth;
    const isFuture = month > currentMonth;

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Get goals for this month (both month-specific and all-month goals)
    const { goalsByMonth, allMonthGoals } = getGoalsByMonth(skill);
    const monthSpecificGoals = goalsByMonth.get(month) || [];
    
    // Combine month-specific goals with all-month goals
    const goalsForMonth = [...monthSpecificGoals, ...allMonthGoals];

    return {
      month,
      monthName: monthNames[month],
      isCurrent,
      isPast,
      isFuture,
      goals: goalsForMonth.length > 0 ? goalsForMonth : undefined
    };
  };

  // Get month data for non-mapping goals
  const getMonthDataForNonMappingGoals = (month: number): { goals?: Goal[] } => {
    const { goalsByMonth, allMonthGoals } = getNonMappingGoalsByMonth();
    const monthSpecificGoals = goalsByMonth.get(month) || [];
    
    // Combine month-specific goals with all-month goals
    const goalsForMonth = [...monthSpecificGoals, ...allMonthGoals];

    return {
      goals: goalsForMonth.length > 0 ? goalsForMonth : undefined
    };
  };


  if (skills.length === 0) {
    return (
      <div className="skillroadmap-container">
        <div className="skillroadmap-empty-state">
          <h3>No skills yet</h3>
          <p>Create skills to see your roadmap</p>
        </div>
      </div>
    );
  }

  // Generate array of 12 months
  const months = Array.from({ length: 12 }, (_, i) => i);
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Render monthly view
  if (viewMode === 'months') {
    return (
      <div className="skillroadmap-container">
        <div className="skillroadmap-timeline">
          {/* Header */}
          <div className="skillroadmap-header">
            <div className="skillroadmap-header-skill-column">Skill</div>
            <div className="skillroadmap-header-months">
              {months.map((month) => {
                const isCurrentMonth = month === currentMonth;
                return (
                  <div
                    key={month}
                    className={`skillroadmap-month-header ${isCurrentMonth ? 'current-month' : ''}`}
                  >
                    {monthNames[month]}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Skills Rows */}
          <div className="skillroadmap-skills">
            {sortedSkills.map((skill) => {
              const skillColor = getSkillColor(skill);
              const rowStyle: React.CSSProperties = {
                backgroundColor: skillColor || '#ffffff',
                ...(skillColor ? { borderLeft: `3px solid ${skillColor}` } : {})
              };
              
              return (
                <div 
                  key={skill.id} 
                  className="skillroadmap-skill-row"
                  style={rowStyle}
                >
                  <div 
                    className="skillroadmap-skill-name skillroadmap-skill-name-clickable"
                    onClick={() => navigate(`/skillmap/${skill.id}/edit`)}
                  >
                    {skill.name}
                  </div>
                  <div className="skillroadmap-skill-timeline">
                    {months.map((month) => {
                      const monthData = getMonthDataForSkill(skill, month);

                      return (
                        <div key={month} className="skillroadmap-month-column">
                          {monthData.goals && monthData.goals.length > 0 ? (
                            <div
                              className={`skillroadmap-milestone-node ${
                                monthData.isPast
                                  ? 'past'
                                  : monthData.isCurrent
                                  ? 'current'
                                  : 'future'
                              }`}
                              title={`${skill.name} - ${monthData.monthName} ${currentYear}`}
                            >
                              <div className="skillroadmap-goal-titles">
                                {monthData.goals.map((goal) => (
                                  <div 
                                    key={goal.id} 
                                    className="skillroadmap-goal-title skillroadmap-goal-title-clickable"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/goals/${goal.id}`);
                                    }}
                                  >
                                    - {goal.title}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="skillroadmap-milestone-node planned" style={{ opacity: 0.3 }}>
                              <div className="skillroadmap-goal-title">—</div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Non-Mapping Goals Row */}
          {nonMappingGoals && nonMappingGoals.length > 0 && (
            <div className="skillroadmap-skill-row skillroadmap-non-mapping-row">
              <div className="skillroadmap-skill-name">
                Other Goals
              </div>
              <div className="skillroadmap-skill-timeline">
                {months.map((month) => {
                  const monthData = getMonthDataForNonMappingGoals(month);
                  const isPast = month < currentMonth;
                  const isCurrent = month === currentMonth;
                  
                  return (
                    <div key={month} className="skillroadmap-month-column">
                      {monthData.goals && monthData.goals.length > 0 ? (
                        <div
                          className={`skillroadmap-milestone-node ${
                            isPast
                              ? 'past'
                              : isCurrent
                              ? 'current'
                              : 'future'
                          }`}
                          title={`Other Goals - ${monthNames[month]} ${currentYear}`}
                        >
                          <div className="skillroadmap-goal-titles">
                            {monthData.goals.map((goal) => (
                              <div 
                                key={goal.id} 
                                className="skillroadmap-goal-title skillroadmap-goal-title-clickable"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/goals/${goal.id}`);
                                }}
                              >
                                - {goal.title}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="skillroadmap-milestone-node planned" style={{ opacity: 0.3 }}>
                          <div className="skillroadmap-goal-title">—</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Render yearly view (default)
  const { years } = getYearRange();

  return (
    <div className="skillroadmap-container">
      <div className="skillroadmap-timeline">
        {/* Header */}
        <div className="skillroadmap-header">
          <div className="skillroadmap-header-skill-column">Skill</div>
          <div className="skillroadmap-header-years">
            {years.map((year) => (
              <div
                key={year}
                className={`skillroadmap-year-header skillroadmap-year-header-clickable ${year === currentYear ? 'current-year' : ''}`}
                onClick={() => navigate(`/skillmap/${year}`)}
              >
                {year}
              </div>
            ))}
          </div>
        </div>

        {/* Skills Rows */}
        <div className="skillroadmap-skills">
          {sortedSkills.map((skill) => {
            const skillColor = getSkillColor(skill);
            const rowStyle: React.CSSProperties = {
              backgroundColor: skillColor || '#ffffff',
              ...(skillColor ? { borderLeft: `3px solid ${skillColor}` } : {})
            };
            
            return (
            <div 
              key={skill.id} 
              className="skillroadmap-skill-row"
              style={rowStyle}
            >
              <div 
                className="skillroadmap-skill-name skillroadmap-skill-name-clickable"
                onClick={() => navigate(`/skillmap/${skill.id}/edit`)}
              >
                {skill.name}
              </div>
              <div className="skillroadmap-skill-timeline">
                {years.map((year) => {
                  const yearData = getYearDataForSkill(skill, year);
                  
                  // Debug logging
                  if (skill.linkedGoals && skill.linkedGoals.length > 0) {
                    console.debug(`Skill "${skill.name}" year ${year}:`, {
                      hasGoals: !!yearData.goals,
                      goalsCount: yearData.goals?.length || 0,
                      goals: yearData.goals?.map(g => ({ title: g.title, targetDate: g.targetDate }))
                    });
                  }

                  return (
                    <div key={year} className="skillroadmap-year-column">
                      {yearData.goals && yearData.goals.length > 0 ? (
                        <div
                          className={`skillroadmap-milestone-node ${
                            yearData.isPast
                              ? 'past'
                              : yearData.isCurrent
                              ? 'current'
                              : 'future'
                          }`}
                          title={`${skill.name} - ${year}`}
                        >
                          <div className="skillroadmap-goal-titles">
                            {yearData.goals.map((goal) => (
                              <div 
                                key={goal.id} 
                                className="skillroadmap-goal-title skillroadmap-goal-title-clickable"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/goals/${goal.id}`);
                                }}
                              >
                                - {goal.title}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="skillroadmap-milestone-node planned" style={{ opacity: 0.3 }}>
                          <div className="skillroadmap-goal-title">—</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            );
          })}
        </div>

        {/* Non-Mapping Goals Row */}
        {nonMappingGoals && nonMappingGoals.length > 0 && (
          <div className="skillroadmap-skill-row skillroadmap-non-mapping-row">
            <div className="skillroadmap-skill-name">
              Other Goals
            </div>
            <div className="skillroadmap-skill-timeline">
              {years.map((year) => {
                const yearData = getYearDataForNonMappingGoals(year);
                const currentYear = new Date().getFullYear();
                const isPast = year < currentYear;
                const isCurrent = year === currentYear;
                
                return (
                  <div key={year} className="skillroadmap-year-column">
                    {yearData.goals && yearData.goals.length > 0 ? (
                      <div
                        className={`skillroadmap-milestone-node ${
                          isPast
                            ? 'past'
                            : isCurrent
                            ? 'current'
                            : 'future'
                        }`}
                        title={`Other Goals - ${year}`}
                      >
                        <div className="skillroadmap-goal-titles">
                          {yearData.goals.map((goal) => (
                            <div 
                              key={goal.id} 
                              className="skillroadmap-goal-title skillroadmap-goal-title-clickable"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/goals/${goal.id}`);
                              }}
                            >
                              - {goal.title}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="skillroadmap-milestone-node planned" style={{ opacity: 0.3 }}>
                        <div className="skillroadmap-goal-title">—</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SkillRoadmap;

